
import { Component, Vue, Prop, Emit } from "vue-property-decorator"
import { State } from "vuex-class"

import PictureSelect from "./PictureSelect.vue"
import { User } from "@/types"
import api from "@/services/api"
import { BaseState } from "@/store"
import { CurrentUser } from "@/types/user"
import Guid from "@/scripts/guid"

	@Component({
		components: {
			"picture-select": PictureSelect
		}
	})
export default class UserProfilePictureModal extends Vue {
		public $refs!: {
			fileInput: HTMLInputElement;
		};

		@Prop({ type: Boolean, default: false }) public readonly visible!: boolean;
		@Prop({ type: Object, default: null }) public readonly userDetails!: User;
		@Prop({ type: Boolean, default: false }) public readonly profilePictureSet!: boolean;

		@State((state: BaseState) => state.me) private readonly me!: CurrentUser;

		private profilePicture: File | string | null = null;
		private profilePictureChanged: boolean = false;
		private profilePictureGuid: string = Guid.newGuid();

		private uploadPercentage: number = 0;
		private alertErrorMessage: string = "";
		private alertCountdown: number = 0;

		/**
		 * @summary Gets the modal title to display.
		 */
		private get modalTitle (): string {
			if (!this.me || !this.userDetails) {
				return ""
			}

			if (this.me.id === this.userDetails.id) {
				return "Update your profile picture"
			} else {
				return `Update ${this.userDetails.name}'s profile picture`
			}
		}

		/**
		 * @summary Mounted lifecycle hook.
		 */
		private async mounted (): Promise<void> {
			if (!this.userDetails) {
				return
			}

			this.setProfilePicture()
		}

		/**
		 * @summary Updates the profile picture displayed within the modal.
		 */
		private setProfilePicture (): void {
			this.profilePictureGuid = Guid.newGuid()
			this.profilePicture = this.userDetails.pictureUrl || `${api.pictureUrl(this.userDetails.id)}?p=${this.profilePictureGuid}`
		}

		/**
		 * @summary Handler for when the user selects a new picture.
		 */
		private onProfilePictureUpdated (newPicture: File): void {
			this.profilePictureChanged = true
			this.profilePicture = newPicture
		}

		/**
		 * @summary Handler for when the modal is made visible, refreshes the profile picture.
		 */
		private onModalShown (): void {
			this.setProfilePicture()
		}

		/**
		 * @summary Handler for when the user closes the modal, resets state etc.
		 */
		@Emit("hidden")
		private onModalHidden (): void {
			this.profilePictureChanged = false
			this.profilePicture = this.userDetails.pictureUrl || api.pictureUrl(this.userDetails.id)
		}

		/**
		 * @summary Handler for when the user saves the new profile picture.
		 */
		private async onSave (): Promise<void> {
			if (!this.profilePictureChanged || this.profilePicture == null || typeof (this.profilePicture) !== "object") {
				return
			}

			const uploadFormData = new FormData()
			uploadFormData.append("file", this.profilePicture)

			try {
				await api.uploadUserImage(this.userDetails.id, uploadFormData, (progressEvent) => {
					this.uploadPercentage = Math.round(progressEvent.loaded * 100 / progressEvent.total)
				})

				this.$emit("update-profile-picture")
				this.$emit("hidden")
			} catch (err) {
				this.alertErrorMessage = "Unable to upload your selected image, please ensure it's a supported file type."
				this.alertCountdown = 10
				console.log(`An error occurred when uploading the profile picture: ${err}`)
			}
		}

		/**
		 * @summary Handler for when the user clicks the delete button, emits an event to clear the profile picture.
		 */
		private async removeSavedImage (): Promise<void> {
			if (!this.userDetails) {
				return
			}

			this.$emit("delete-profile-picture")
			this.$emit("hidden")
		}

		/**
		 * @summary Handler for when the alert emits a dismiss count down event.
		 */
		private alertCountdownChanged (countdown: number) {
			this.alertCountdown = countdown
		}

		/**
		 * @summary Triggers the picture select file open dialog.
		 */
		private openPictureSelect (): void {
			this.$refs.fileInput.click()
		}

		private onQuickPictureSelected (event: Event | any): void {
			if (!event || !event.target || !event.target.files) {
				return
			}

			this.profilePictureChanged = true
			this.profilePicture = event.target.files[0]

			this.onSave()
			this.onModalHidden()
		}
}
